import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

import { IDE_TYPE } from "../../../constants/common";
import lessonServices from "../../../services/lessonServices";
import studentServices from "../../../services/studentServices";
import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import TeacherPythonIDEWrapper from "../../IDEs/wrappers/teacher/TeacherPythonIDEWrapper";
import TeacherSmoothyIDEWrapper from "../../IDEs/wrappers/teacher/TeacherSmoothyIDEWrapper";
import TeacherWebIDEWrapper from "../../IDEs/wrappers/teacher/TeacherWebIDEWrapper";
import Modal from "../../Modal";

const CodeSubmissionsModal = forwardRef(({ type, selectedProgram, onSubmit }, ref) => {
  const { t } = useTranslation();
  const lang = getLanguage();

  const [IDEType, setIDEType] = useState("");
  const [selectedLessonID, setSelectedLessonID] = useState("");
  const [selectedProjectID, setSelectedProjectID] = useState("");
  const [challengeData, setChallengeData] = useState(null);
  const [lessonsData, setLessonsData] = useState([]);
  const [lessonName, setLessonName] = useState("");
  const [studentName, setStudentName] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [lessonIDs, setLessonIDs] = useState([]);
  const [codeData, setCodeData] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);

  useImperativeHandle(ref, () => ({
    openModal(lessonID, lessonsData, studentName, IDEType, index, selectedLesson) {
      setSelectedLessonID(lessonID);
      setIDEType(IDEType);
      setLessonsData(lessonsData);

      if (type == "lesson") {
        setStudentName(lessonsData.find((lesson) => lesson.id === lessonID)?.student?.name || "");

        if (selectedProgram != "playground") {
          setLessonName(selectedLesson?.label);
          setSelectedProjectID(selectedLesson.value);
        } else {
          setLessonName(lessonsData.find((lesson) => lesson.id === lessonID)?.name || "");
        }
      } else {
        setStudentName(studentName);
        setLessonName(lessonsData.find((lesson) => lesson.id === lessonID)?.name || "");
        setSelectedProjectID(
          lessonsData.find((lesson) => lesson.lesson_id === selectedLesson?.lesson_id)?.lesson_id || ""
        );
      }
      setCurrentLessonIndex(index);

      const lessonIDsList = lessonsData.map((item) => item?.id);
      setLessonIDs(lessonIDsList);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  // Change lesson when navigating
  const goToNextLesson = () => {
    if (currentLessonIndex < lessonIDs.length - 1) {
      setCurrentLessonIndex(currentLessonIndex + 1);
      setSelectedLessonID(lessonIDs[currentLessonIndex + 1]); // Update the lesson ID
    }
  };

  const goToPreviousLesson = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex(currentLessonIndex - 1);
      setSelectedLessonID(lessonIDs[currentLessonIndex - 1]); // Update the lesson ID
    }
  };

  const getCodeSolution = async () => {
    try {
      const response = await studentServices.getStudentProject(selectedLessonID);
      setCodeData(response);
    } catch (error) {
      console.error("Error fetching  data:", error);
    } finally {
    }
  };

  const onSubmitTwo = () => {
    getCodeSolution();
    onSubmit();
  };

  const getProjectGoal = async (id) => {
    try {
      const response = await lessonServices.getLessonProject(id, { type: selectedProgram });
      setChallengeData(response);
    } catch (error) {
      console.error("Error fetching  data:", error);
    } finally {
    }
  };

  useEffect(() => {
    if ((lessonsData, isOpen)) {
      const currentLesson = lessonsData.find((lesson) => lesson.id === selectedLessonID);

      if (type === "lesson") {
        setStudentName(currentLesson?.student?.name || "");
        getCodeSolution();
        if (selectedProgram == "challenge") {
          getProjectGoal(currentLesson?.lesson_project?.id);
        } else {
          setChallengeData(null);
        }
        if (selectedProgram == "graduation") {
          getProjectGoal(selectedProjectID);
        } else {
          setChallengeData(null);
        }
      } else {
        setLessonName(currentLesson?.name || "");
        setSelectedProjectID(currentLesson?.lesson_id || "");
        getCodeSolution();
        if (selectedProgram == "challenge") {
          getProjectGoal(currentLesson?.lesson_project_id);
        } else {
          setChallengeData(null);
        }
        if (selectedProgram == "graduation") {
          getProjectGoal(currentLesson?.lesson_id);
        } else {
          setChallengeData(null);
        }
      }
    }
  }, [selectedLessonID, lessonsData, isOpen]);

  return (
    <Modal
      isFullScreen
      isOpen={isOpen}
      onClose={handleCloseModal}
      footer={
        <div className="flex justify-between">
          <Button
            iconLeft={<MdChevronLeft size={20} className={lang == "ar" && "rotate-180"} />}
            variant="text"
            onClick={goToPreviousLesson}
            disabled={currentLessonIndex === 0}
            label={t("previous")}
          />
          <Button
            iconRight={<MdChevronRight size={20} className={lang == "ar" && "rotate-180"} />}
            variant="text"
            onClick={goToNextLesson}
            disabled={currentLessonIndex === lessonIDs.length - 1}
            label={t("next")}
          />
        </div>
      }
    >
      {codeData && IDEType === IDE_TYPE.SMOOTHY && (
        <TeacherSmoothyIDEWrapper
          onSubmit={onSubmitTwo}
          type="submissions"
          codeData={codeData}
          lessonName={lessonName}
          lessonID={selectedProjectID}
          studentName={studentName}
          isPlaygroundProject={selectedProgram == "playground"}
          projectGoal={{
            projectGoal: challengeData?.challenge_statement,
            name: challengeData?.name,
            image: challengeData?.image,
          }}
        />
      )}

      {codeData && IDEType === IDE_TYPE.WEB && (
        <TeacherWebIDEWrapper
          onSubmit={onSubmitTwo}
          type="submissions"
          codeData={codeData}
          lessonName={lessonName}
          lessonID={selectedProjectID}
          studentName={studentName}
          isPlaygroundProject={selectedProgram == "playground"}
          projectGoal={{
            projectGoal: challengeData?.challenge_statement,
            name: challengeData?.name,
            image: challengeData?.image,
          }}
        />
      )}

      {codeData && IDEType === IDE_TYPE.PYTHON && (
        <TeacherPythonIDEWrapper
          onSubmit={onSubmitTwo}
          type="submissions"
          codeData={codeData}
          lessonName={lessonName}
          lessonID={selectedProjectID}
          studentName={studentName}
          isPlaygroundProject={selectedProgram == "playground"}
          projectGoal={{
            projectGoal: challengeData?.challenge_statement,
            name: challengeData?.name,
            image: challengeData?.image,
          }}
        />
      )}
    </Modal>
  );
});

export default CodeSubmissionsModal;
